import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import {
  CalendarPlus,
  Check,
  ChevronDown,
  CircleCheck,
  CircleSlash,
  FilePenLine,
  Layers,
  Pencil,
  RotateCcw,
  Send,
  Trash2,
  Undo2,
} from 'lucide-react';

import { approveCustomerOrder } from '#/api/crmMgmt/customerOrder/approve';
import { closeCustomerOrder } from '#/api/crmMgmt/customerOrder/close';
import { deleteDraftCustomerOrder } from '#/api/crmMgmt/customerOrder/deleteDraft';
import { rejectCustomerOrder } from '#/api/crmMgmt/customerOrder/reject';
import { reopenCustomerOrder } from '#/api/crmMgmt/customerOrder/reopen';
import { newIdempotencyKey } from '#/api/crmMgmt/customerOrder/collection/shared';
import { submitCustomerOrder } from '#/api/crmMgmt/customerOrder/submit';
import { terminateCustomerOrder } from '#/api/crmMgmt/customerOrder/terminate';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#/modules/commonComponents/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';
import CustomerOrderCloseDialog from '#/modules/crmMgmt/customerOrder/components/CustomerOrderCloseDialog';
import { useCan } from '#/modules/infra/auth/useCan';
import {
  formatCustomerOrderCloseBlockedMessage,
  getCustomerOrderApiErrorMessage,
  getCustomerOrderCloseBlockedDetails,
  isCustomerOrderVersionConflict,
} from '#/modules/crmMgmt/customerOrder/helper/apiError';
import {
  canApproveCustomerOrder,
  canCloseCustomerOrder,
  canCreateManufacturingWorkOrdersFromCustomerOrder,
  canCreateSalesOrderFromCustomerOrder,
  canDeleteDraftCustomerOrder,
  canEditCustomerOrderDraft,
  canRejectCustomerOrder,
  canReopenCustomerOrder,
  canSubmitCustomerOrder,
  canTerminateCustomerOrder,
  canUpdateCustomerOrderRemark,
} from '#/modules/crmMgmt/customerOrder/helper/customerOrderStatus';
import DocumentRejectReasonDialog from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import {
  pickAvailableWorkflowExposedIds,
  resolveLgDesktopLayout,
  resolveMdDesktopLayout,
  resolveMobileLayout,
} from '#/modules/imsMgmt/receipts/shared/documentCommandBarLayout';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import { toast } from '#/utils/toast';
import {
  customerOrderDetailQueryKey,
  customerOrderListQueryKeyPrefix,
} from '#/utils/queryKeys';

const WORKFLOW_EXPOSED_ORDER = [
  'submit',
  'approve',
  'reject',
  'close',
  'reopen',
  'terminate',
  'deleteDraft',
];

const ACTION_GROUPS = [
  { key: 'content', label: '單據內容', ids: ['edit', 'editRemark', 'manufacturingMaterials'] },
  {
    key: 'fulfillment',
    label: '履約',
    ids: ['createSalesOrder', 'submit', 'approve', 'reject', 'close', 'reopen'],
  },
  { key: 'status', label: '單據狀態', ids: ['terminate', 'deleteDraft'] },
];

const ACTION_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  to: PropTypes.string,
  onSelect: PropTypes.func,
  destructive: PropTypes.bool,
  disabled: PropTypes.bool,
  available: PropTypes.bool,
  showExposedIcon: PropTypes.bool,
  exposedVariant: PropTypes.string,
  exposedClassName: PropTypes.string,
  tooltip: PropTypes.string,
});

function buildCreateSalesOrderPath(id) {
  if (!id) {
    return undefined;
  }
  return `${ROUTES.salesOrderCreate}?customerOrderId=${encodeURIComponent(id)}`;
}

function getExposedActionIds(actionMap) {
  return pickAvailableWorkflowExposedIds(actionMap, WORKFLOW_EXPOSED_ORDER);
}

function runAfterMenuClose(callback) {
  window.setTimeout(() => {
    callback?.();
  }, 0);
}

function wrapWithTooltip(tooltip, node) {
  if (!tooltip) {
    return node;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {node}
      </TooltipTrigger>
      <TooltipContent side="bottom">{tooltip}</TooltipContent>
    </Tooltip>
  );
}

function CustomerOrderExposedActionButton({ action, isPending }) {
  const Icon = action.icon;
  const disabled = Boolean(isPending || action.disabled);

  const button = action.to && !disabled ? (
    <Button
      asChild
      variant={action.exposedVariant || 'default'}
      className={cn(action.exposedClassName)}
    >
      <Link to={action.to} aria-label={action.label}>
        {action.showExposedIcon && Icon ? (
          <Icon className="size-3.5" aria-hidden />
        ) : null}
        {action.label}
      </Link>
    </Button>
  ) : (
    <Button
      type="button"
      variant={action.destructive ? 'destructive' : (action.exposedVariant || 'default')}
      className={cn(
        action.destructive && DESTRUCTIVE_SOLID_BUTTON_CLASS,
        action.exposedClassName,
      )}
      disabled={disabled}
      onClick={() => action.onSelect?.()}
    >
      {action.showExposedIcon && Icon ? (
        <Icon className="size-3.5" aria-hidden />
      ) : null}
      {action.label}
    </Button>
  );

  return wrapWithTooltip(action.tooltip, button);
}

CustomerOrderExposedActionButton.propTypes = {
  action: ACTION_SHAPE.isRequired,
  isPending: PropTypes.bool,
};

function CustomerOrderMenuItem({ action, isPending }) {
  const Icon = action.icon;
  const disabled = Boolean(isPending || action.disabled);
  const iconNode = Icon ? <Icon className="size-4" aria-hidden /> : null;

  const item = action.to && !disabled ? (
    <DropdownMenuItem
      asChild
      variant={action.destructive ? 'destructive' : 'default'}
    >
      <Link to={action.to}>
        {iconNode}
        {action.label}
      </Link>
    </DropdownMenuItem>
  ) : (
    <DropdownMenuItem
      variant={action.destructive ? 'destructive' : 'default'}
      disabled={disabled}
      onSelect={() => {
        if (disabled) {
          return;
        }
        runAfterMenuClose(action.onSelect);
      }}
    >
      {iconNode}
      {action.label}
    </DropdownMenuItem>
  );

  return wrapWithTooltip(action.tooltip, item);
}

CustomerOrderMenuItem.propTypes = {
  action: ACTION_SHAPE.isRequired,
  isPending: PropTypes.bool,
};

function CustomerOrderActionsMenu({
  triggerLabel,
  triggerAriaLabel,
  groups,
  isPending,
}) {
  if (!groups.length) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={isPending}
          aria-label={triggerAriaLabel || triggerLabel}
        >
          {triggerLabel}
          <ChevronDown className="size-3.5" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={cn('w-auto min-w-44')}>
        {groups.map((group, index) => (
          <Fragment key={group.key}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
            {group.items.map((action) => (
              <CustomerOrderMenuItem
                key={action.id}
                action={action}
                isPending={isPending}
              />
            ))}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

CustomerOrderActionsMenu.propTypes = {
  triggerLabel: PropTypes.string.isRequired,
  triggerAriaLabel: PropTypes.string,
  groups: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(ACTION_SHAPE).isRequired,
  })).isRequired,
  isPending: PropTypes.bool,
};

const CustomerOrderCommandBar = ({ detail, onRefetch }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  // 一次重新開啟操作（含失敗重送）共用一把 key；成功後換新
  const [reopenIdempotencyKey, setReopenIdempotencyKey] = useState(newIdempotencyKey);
  const canCreate = useCan(PERM.CUSTOMER_ORDER_CREATE);
  const canUpdate = useCan(PERM.CUSTOMER_ORDER_UPDATE);
  const canReview = useCan(PERM.CUSTOMER_ORDER_REVIEW);
  const canCreateSalesOrder = useCan(PERM.SALES_ORDER_CREATE);
  const canCreateWorkOrder = useCan(PERM.WORK_ORDER_CREATE);

  const id = detail?.id;
  const updatedAt = detail?.record?.updatedAt ?? detail?.updatedAt;
  const status = detail?.status;

  async function invalidateAll() {
    await queryClient.invalidateQueries({
      queryKey: customerOrderListQueryKeyPrefix(),
    });
    if (id) {
      await queryClient.invalidateQueries({
        queryKey: customerOrderDetailQueryKey(id),
      });
    }
    await onRefetch?.();
  }

  const commandMutation = useMutation({
    retry: false,
    mutationFn: async ({ action, payload }) => {
      if (action === 'submit') {
        return submitCustomerOrder(id, payload);
      }
      if (action === 'approve') {
        return approveCustomerOrder(id, payload);
      }
      if (action === 'reject') {
        return rejectCustomerOrder(id, payload);
      }
      if (action === 'close') {
        return closeCustomerOrder(id, payload);
      }
      if (action === 'terminate') {
        return terminateCustomerOrder(id, payload);
      }
      if (action === 'reopen') {
        return reopenCustomerOrder(id, payload, { idempotencyKey: reopenIdempotencyKey });
      }
      if (action === 'deleteDraft') {
        return deleteDraftCustomerOrder(id, payload);
      }
      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result, variables) => {
      toast.success(result?.message || '操作成功');
      if (variables.action === 'reject') {
        setRejectDialogOpen(false);
      }
      if (variables.action === 'terminate') {
        setTerminateDialogOpen(false);
      }
      if (variables.action === 'close') {
        setCloseDialogOpen(false);
      }
      if (variables.action === 'reopen') {
        setReopenDialogOpen(false);
        setReopenIdempotencyKey(newIdempotencyKey());
      }
      if (variables.action === 'deleteDraft') {
        await queryClient.invalidateQueries({
          queryKey: customerOrderListQueryKeyPrefix(),
        });
        navigate(ROUTES.customerOrder);
        return;
      }
      if (id && result?.data) {
        queryClient.setQueryData(customerOrderDetailQueryKey(id), {
          message: result.message,
          route: result.route,
          data: result.data,
        });
      }
      await invalidateAll();
    },
    onError: async (error, variables) => {
      if (isCustomerOrderVersionConflict(error)) {
        toast.error(getCustomerOrderApiErrorMessage(error));
        await onRefetch?.();
        return;
      }

      const blocked = getCustomerOrderCloseBlockedDetails(error);
      if (variables?.action === 'close' && blocked) {
        toast.error(formatCustomerOrderCloseBlockedMessage(blocked));
        await onRefetch?.();
        return;
      }

      toast.error(getCustomerOrderApiErrorMessage(error));
    },
  });

  const isPending = commandMutation.isPending;

  async function handleSubmit() {
    const ok = await confirm({
      title: '提交客戶訂單',
      message: '提交後進入待核准，確定提交？',
      confirmText: '提交',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'submit', payload: { updatedAt } });
  }

  async function handleApprove() {
    const ok = await confirm({
      title: '核准客戶訂單',
      message: '核准後將建立客戶／產品／組成快照，確定核准？',
      confirmText: '核准',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'approve', payload: { updatedAt } });
  }

  function handleRejectConfirm(rejectReason) {
    commandMutation.mutate({
      action: 'reject',
      payload: { updatedAt, rejectReason },
    });
  }

  function handleTerminateConfirm(terminateReason) {
    commandMutation.mutate({
      action: 'terminate',
      payload: { updatedAt, terminateReason },
    });
  }

  function handleCloseConfirm({ closeReason }) {
    commandMutation.mutate({
      action: 'close',
      payload: {
        updatedAt,
        closeReason,
      },
    });
  }

  function handleReopenConfirm(reopenReason) {
    commandMutation.mutate({
      action: 'reopen',
      payload: { updatedAt, reopenReason },
    });
  }

  async function handleDeleteDraft() {
    const ok = await confirm({
      title: '刪除草稿',
      message: '將實體刪除此草稿（不可復原）。若已有銷貨單引用則無法刪除。確定刪除？',
      confirmText: '刪除草稿',
      destructive: true,
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'deleteDraft', payload: { updatedAt } });
  }

  const actionMap = {
    edit: {
      id: 'edit',
      label: '編輯',
      icon: Pencil,
      to: id ? ROUTES.customerOrderEdit(id) : undefined,
      available: canUpdate && canEditCustomerOrderDraft(status),
      showExposedIcon: true,
      exposedVariant: 'default',
      exposedClassName: 'document-detail-edit-action',
    },
    editRemark: {
      id: 'editRemark',
      label: '編輯備註',
      icon: FilePenLine,
      to: id ? ROUTES.customerOrderRemark(id) : undefined,
      available: canUpdate && canUpdateCustomerOrderRemark(status),
      showExposedIcon: true,
      exposedVariant: 'outline',
    },
    manufacturingMaterials: {
      id: 'manufacturingMaterials',
      label: '建立多階層工單',
      icon: Layers,
      to: id ? ROUTES.customerOrderManufacturingMaterials(id) : undefined,
      available: Boolean(id)
        && canCreateWorkOrder
        && canCreateManufacturingWorkOrdersFromCustomerOrder(status),
      showExposedIcon: true,
      exposedVariant: 'outline',
      tooltip: '依用料需求建立多階層工單草稿',
    },
    createSalesOrder: {
      id: 'createSalesOrder',
      label: '建立銷貨單',
      icon: CalendarPlus,
      to: buildCreateSalesOrderPath(id),
      available: canCreateSalesOrder && canCreateSalesOrderFromCustomerOrder(status),
      showExposedIcon: true,
      exposedVariant: 'default',
      tooltip: '依客訂預填建立 DRAFT 銷貨單',
    },
    submit: {
      id: 'submit',
      label: '提交',
      icon: Send,
      available: canCreate && canSubmitCustomerOrder(status),
      onSelect: handleSubmit,
    },
    approve: {
      id: 'approve',
      label: '核准',
      icon: Check,
      available: canReview && canApproveCustomerOrder(status),
      onSelect: handleApprove,
    },
    reject: {
      id: 'reject',
      label: '退回',
      icon: Undo2,
      available: canReview && canRejectCustomerOrder(status),
      destructive: true,
      onSelect: () => setRejectDialogOpen(true),
    },
    close: {
      id: 'close',
      label: '結案',
      icon: CircleCheck,
      available: canReview && canCloseCustomerOrder(status, detail),
      onSelect: () => setCloseDialogOpen(true),
    },
    reopen: {
      id: 'reopen',
      label: '重新開啟',
      icon: RotateCcw,
      available: canReview && canReopenCustomerOrder(status),
      onSelect: () => setReopenDialogOpen(true),
      tooltip: '還原為結案前狀態，以便沖銷或退款',
    },
    terminate: {
      id: 'terminate',
      label: '中止',
      icon: CircleSlash,
      available: canReview && canTerminateCustomerOrder(status),
      destructive: true,
      onSelect: () => setTerminateDialogOpen(true),
    },
    deleteDraft: {
      id: 'deleteDraft',
      label: '刪除草稿',
      icon: Trash2,
      available: canCreate && canDeleteDraftCustomerOrder(status),
      destructive: true,
      onSelect: handleDeleteDraft,
    },
  };

  const mobileLayout = resolveMobileLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
  });
  const mdLayout = resolveMdDesktopLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
    getExposedActionIds,
  });
  const lgLayout = resolveLgDesktopLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
    getExposedActionIds,
  });

  function renderLayoutRow(
    layout,
    { triggerLabel = '更多', triggerAriaLabel = '更多操作' } = {},
  ) {
    return (
      <>
        {layout.flatActions.map((action) => (
          <CustomerOrderExposedActionButton
            key={action.id}
            action={action}
            isPending={isPending}
          />
        ))}
        {layout.showMoreMenu ? (
          <CustomerOrderActionsMenu
            triggerLabel={triggerLabel}
            triggerAriaLabel={triggerAriaLabel}
            groups={layout.menuGroups}
            isPending={isPending}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      {ConfirmDialogHost}
      <DocumentRejectReasonDialog
        open={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
        title="退回客戶訂單"
        description="退回後將恢復為草稿，確定退回？"
        confirmText="退回"
        isPending={isPending}
        onConfirm={handleRejectConfirm}
      />
      <DocumentRejectReasonDialog
        open={terminateDialogOpen}
        onOpenChange={setTerminateDialogOpen}
        title="中止客戶訂單"
        description="僅用於尚未達結案條件（PAID 累計量不足）的訂單；中止原因必填。確定中止？"
        confirmText="中止"
        reasonLabel="中止原因"
        emptyErrorMessage="請填寫中止原因"
        isPending={isPending}
        onConfirm={handleTerminateConfirm}
      />
      <DocumentRejectReasonDialog
        open={reopenDialogOpen}
        onOpenChange={setReopenDialogOpen}
        title="重新開啟客戶訂單"
        description="將還原為結案前狀態（出貨與收款紀錄不變），之後才能沖銷或退款。確定重新開啟？"
        confirmText="重新開啟"
        reasonLabel="重新開啟原因"
        emptyErrorMessage="請填寫重新開啟原因"
        isPending={isPending}
        onConfirm={handleReopenConfirm}
      />
      <CustomerOrderCloseDialog
        open={closeDialogOpen}
        onOpenChange={setCloseDialogOpen}
        isPending={isPending}
        onConfirm={handleCloseConfirm}
      />

      <div className={cn('flex items-center justify-end gap-2')}>
        <div className={cn('flex md:hidden', 'items-center gap-2')}>
          {renderLayoutRow(mobileLayout, {
            triggerLabel: '操作',
            triggerAriaLabel: '單據操作',
          })}
        </div>
        <div className={cn('hidden md:flex lg:hidden', 'items-center gap-2')}>
          {renderLayoutRow(mdLayout)}
        </div>
        <div className={cn('hidden lg:flex', 'items-center gap-2')}>
          {renderLayoutRow(lgLayout)}
        </div>
      </div>
    </>
  );
};

CustomerOrderCommandBar.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    updatedAt: PropTypes.string,
    record: PropTypes.shape({
      updatedAt: PropTypes.string,
    }),
    lines: PropTypes.arrayOf(PropTypes.object),
    salesOrders: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onRefetch: PropTypes.func,
};

export default CustomerOrderCommandBar;
